import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { registerMutation, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { getCollection, getRawCollection } from '@adornis/baseql/server/collections.js';
import { context } from '@adornis/baseql/server/context.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet, NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { Names } from '@ils/static/names.js';
import { RESERVATION_TIMEOUT_MS } from '@ils/static/values.js';
import { map } from 'rxjs';
import { ExamDay } from './examdays.js';

/**
 * Represents a reservation for an exam day that is yet to be booked.
 * This entity is attached to the exam day that is reserved.
 * @entity ExamDayReservation
 * @property {string} sessionID - the session from which the reservation is created
 * @property {string} examDayID - the exam day that is reserved
 * @property {Date} activeUntil - the date until the reservation is active
 */
@Entity()
export class ExamDayReservation extends MongoEntity {
  public static override _class = Names.ExamDayReservation._class;
  public static override _collectionName = Names.ExamDayReservation._collectionName;

  @Field(type => ID)
  sessionID!: string;

  @Field(type => ID)
  examDayID!: string;

  @Field(type => Date)
  activeUntil!: Date;

  async startPaymentProcess() {
    this.activeUntil = new Date(Date.now() + RESERVATION_TIMEOUT_MS);
    await this.save();
  }
}

// has to be a function to recalculate the date
const isActiveQuery = () => ({ activeUntil: { $gt: new Date() } });

export const removeReservationsForSession = async (sessionID: string) =>
  getRawCollection<ExamDayReservation>(ExamDayReservation._collectionName).then(c => c.deleteMany({ sessionID }));

const getActiveReservationBySessionAndExamDay = async (sessionID: string, examDayID: string) =>
  getCollection<typeof ExamDayReservation>(ExamDayReservation)
    .then(c => c.findOne({ $and: [{ sessionID }, { examDayID }, isActiveQuery()] }))
    .then(xs => (xs ? (xs as ExamDayReservation) : xs));

const getActiveReservationsByExamDay = async (examDayID: string) =>
  getCollection<typeof ExamDayReservation>(ExamDayReservation)
    .then(c => c.find({ $and: [{ examDayID }, isActiveQuery()] }).toArray())
    .then(xs => xs as ExamDayReservation[]);

const isExamDayReservable = async (
  examDay: NarrowedEntity<ExamDay, { _id: 1; maxCandidates: 1; candidateIDs: 1 }>,
): Promise<boolean> => {
  // check number of available slots in exam day
  if (examDay.maxCandidates <= examDay.candidateIDs.length) return false;
  // check other active reservations for the exam day
  const activeReservations = await getActiveReservationsByExamDay(examDay._id);
  if (examDay.maxCandidates <= examDay.candidateIDs.length + activeReservations.length) return false;
  // there are some slots available
  return true;
};

// Mutation try create reservation
const resolveTryCreateReservation =
  (examDayID: string) =>
  async (gqlFields: BaseQLSelectionSet<ExamDayReservation>): Promise<Maybe<ExamDayReservation>> => {
    const sessionID = context.sessionID;
    // check if session has already reserved that same exam day
    const existingReservation = await getActiveReservationBySessionAndExamDay(sessionID, examDayID);
    if (existingReservation) return existingReservation;
    // get exam day
    const examDay = await getByID<typeof ExamDay>(ExamDay, examDayID)({ _id: 1, maxCandidates: 1, candidateIDs: 1 });
    if (!examDay) return null;
    // check if exam day is reservable, if not return null
    if (!(await isExamDayReservable(examDay))) return null;

    // remove existing reservations for the session
    await removeReservationsForSession(sessionID);
    // create reservation
    const reservation = new ExamDayReservation({
      sessionID,
      examDayID,
      activeUntil: new Date(Date.now() + RESERVATION_TIMEOUT_MS),
    });
    await reservation.create();
    return reservation;
  };

export const tryCreateReservation = registerMutation({
  operationName: 'tryCreateReservation',
  type: () => ExamDayReservation,
  params: [{ name: 'examDayID', type: () => ID }],
  resolve: resolveTryCreateReservation,
});

// Subscribtion reservations by session and exam day
const resolveSubscribeReservationBySessionAndExamDay =
  (examDayID: string) => (gqlFields: BaseQLSelectionSet<ExamDayReservation>) =>
    watchQuery<ExamDayReservation>(ExamDayReservation._collectionName, {
      sessionID: context.sessionID,
      examDayID,
    }).pipe(map(a => (a.length ? (a[0] as ExamDayReservation) : null) as Maybe<ExamDayReservation>));

export const subscribeReservationBySessionAndExamDay = registerSubscription({
  operationName: 'subscribeReservationBySessionAndExamDay',
  type: () => ExamDayReservation,
  params: [{ name: 'examDayID', type: () => ID }],
  resolve: resolveSubscribeReservationBySessionAndExamDay,
});

// Subscription number of active reservations for exam day
export const resolveSubscribeActiveReservationsByExamDayCount = (examDayID: string) => () =>
  getCollection<typeof ExamDayReservation>(ExamDayReservation).then(c =>
    c.countDocuments({ $and: [{ examDayID }, isActiveQuery()] }),
  );

// Mutation remove reservation for session and exam day
export const resolveRemoveReservationBySessionAndExamDay = async (sessionID: string, examDayID: string) =>
  getRawCollection<typeof ExamDayReservation>(ExamDayReservation._collectionName).then(c =>
    c.deleteMany({ $and: [{ sessionID }, { examDayID }] }),
  );
